import type { ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import type { IInfluencer } from '../../../redux/buzzbridge/types';
import type { RateCardPlatformId } from '../utils/rateCardPlatformConfig';
import { InstagramGlyph, TiktokGlyph, YoutubeGlyph } from '../SocialPlatformSvgs';
import { formatSocialHandle } from '../../../utils/socialHandle';

const SURFACE = '#1a1919';
const SURFACE_HI = '#201f1f';
const GOLD = '#ffc965';
const MINT = '#00fdc1';
const ON_VARIANT = '#adaaaa';

function compactCount(value?: number): string | null {
    if (value == null || value <= 0) return null;
    if (value >= 1_000_000) {
        const n = value / 1_000_000;
        return `${n >= 10 ? Math.round(n) : n.toFixed(1).replace(/\.0$/, '')}m`;
    }
    if (value >= 1000) {
        const n = value / 1000;
        return `${n >= 10 ? Math.round(n) : n.toFixed(1).replace(/\.0$/, '')}k`;
    }
    return String(value);
}

export function CreatorSocialPills({
    influencer,
    onLinkAccount,
    onPlatformClick,
}: {
    influencer: IInfluencer;
    onLinkAccount?: () => void;
    onPlatformClick?: (platform: RateCardPlatformId) => void;
}) {
    const pills: {
        key: string;
        platformId: RateCardPlatformId;
        label: string;
        handle: string;
        count: string | null;
        glyph: ReactNode;
    }[] = [];
    if (influencer.instagram?.username) {
        pills.push({
            key: 'ig',
            platformId: 'instagram',
            label: 'Instagram',
            handle: formatSocialHandle(influencer.instagram.username),
            count: compactCount(influencer.instagram.followers),
            glyph: <InstagramGlyph sx={{ width: 16, height: 16 }} color="#fff" />,
        });
    }
    if (influencer.tiktok?.username) {
        pills.push({
            key: 'tt',
            platformId: 'tiktok',
            label: 'TikTok',
            handle: formatSocialHandle(influencer.tiktok.username),
            count: compactCount(influencer.tiktok.followers),
            glyph: <TiktokGlyph sx={{ width: 16, height: 16 }} color="#fff" />,
        });
    }
    if (influencer.youtube?.username) {
        pills.push({
            key: 'yt',
            platformId: 'youtube',
            label: 'YouTube',
            handle: formatSocialHandle(influencer.youtube.username),
            count: compactCount(influencer.youtube.followers),
            glyph: <YoutubeGlyph sx={{ width: 16, height: 16 }} color="#fff" />,
        });
    }

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                overflowX: 'auto',
                pb: 0.5,
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
            }}>
            {pills.map(pill => (
                <Box
                    key={pill.key}
                    component="button"
                    type="button"
                    aria-label={`${pill.label} rate card`}
                    onClick={() => onPlatformClick?.(pill.platformId)}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 999,
                        bgcolor: SURFACE,
                        flexShrink: 0,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                        border: 'none',
                        cursor: onPlatformClick ? 'pointer' : 'default',
                        font: 'inherit',
                        '&:hover': onPlatformClick ? { bgcolor: SURFACE_HI } : undefined,
                    }}>
                    {pill.glyph}
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 12, color: '#fff' }}>
                        {pill.label}
                    </Typography>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 12, color: ON_VARIANT }}>
                        {pill.handle}
                    </Typography>
                    {pill.count ? (
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 11, color: GOLD }}>
                            {pill.count}
                        </Typography>
                    ) : null}
                    <Box
                        sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: MINT,
                            boxShadow: '0 0 6px #00fdc1',
                        }}
                        title="Connected"
                    />
                </Box>
            ))}
            {onLinkAccount ? (
                <Button
                    onClick={onLinkAccount}
                    startIcon={<AddIcon sx={{ fontSize: 14, color: MINT }} />}
                    sx={{
                        flexShrink: 0,
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 999,
                        bgcolor: SURFACE_HI,
                        color: '#fff',
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 11,
                        '&:hover': { bgcolor: '#262626' },
                    }}>
                    Link Account
                </Button>
            ) : null}
        </Box>
    );
}
